import * as Effect from "effect-app/Effect"
import * as S from "effect-app/Schema"
import { DXFFaceDecor } from "./decorSchema.ts"
import { EDGE_DIRECTIONS, FACE_DIRECTIONS } from "./face.ts"
import { DXFColorType, DXFMaterialEffectType, DXFShapeType } from "./types.ts"

export const DXFMeasurements = S.Literals([
  "width",
  "height",
  "depth",
  "diameter",
  "radius",
  "outerRadius",
  "innerRadius",
  "cornerRadius",
  "bevel",
  "shortEdge",
  "offset",
  "topWidth",
  "bottomWidth"
])

export const DXFCameraPreset = S.Literals(["isometric", "top", "bottom", "north", "south", "east", "west"])
export const DXFPreviewControlsVisibility = S.Literals(["always", "focus"])
export type DXFCameraPreset = typeof DXFCameraPreset.Type

const HexColor = S.String.pipe(S.check(S.isPattern(/^#[0-9A-Fa-f]{6}$/)))

const DimensionOptionLink = S.optional(S.Struct({
  controlledBy: S.optionalKey(S.StringId),
  defaultValue: S.optionalKey(S.Finite)
}))

export const DXFFaceDirection = S.Literals(FACE_DIRECTIONS)
export type DXFFaceDirection = typeof DXFFaceDirection.Type

// "length" = along slab's X axis (top face `width`), "width" = along Y (`height`).
// Internal naming is English; German only in user-facing labels.
export const DXFGrainDirection = S.Literals(["length", "width"])
export type DXFGrainDirection = typeof DXFGrainDirection.Type

export const DXFGrainDirectionOption = S.Struct({
  controlledBy: S.StringId,
  directionMapping: S.mutable(S.Array(S.Struct({
    optionItemId: S.StringId,
    direction: DXFGrainDirection
  })))
})
export type DXFGrainDirectionOption = typeof DXFGrainDirectionOption.Type

export const DecorOptionLink = S.Struct({
  controlledBy: S.StringId,
  decorMapping: S.mutable(S.Array(S.Struct({
    optionItemId: S.StringId,
    // nullable: an item may map to "no decor" (and OmegaTaggedUnion needs a null option)
    decor: S.NullOr(DXFFaceDecor)
  })))
})
export type DecorOptionLink = typeof DecorOptionLink.Type

export const DXFEdgeDirection = S.Literals(EDGE_DIRECTIONS)
export type DXFEdgeDirection = typeof DXFEdgeDirection.Type

// One "which sides are banded" link: the selected item of the controlling
// option maps to a set of edge directions. Multiple links are resolved by UNION.
export const EdgeSidesLink = S.Struct({
  controlledBy: S.StringId,
  mapping: S.mutable(S.Array(S.Struct({
    optionItemId: S.StringId,
    directions: S.mutable(S.Array(DXFEdgeDirection))
  })))
})
export type EdgeSidesLink = typeof EdgeSidesLink.Type

export const DXFEdgeBanding = S.Struct({
  // default decor for unbanded edges (raw chipboard); null = fallback slab material
  rawDecor: S.NullOr(DXFFaceDecor),
  // which edges are banded: union of all resolved links
  activeSides: S.mutable(S.Array(EdgeSidesLink)),
  // banded-edge decor: priority chain, first link that resolves wins
  edgeDecor: S.mutable(S.Array(DecorOptionLink))
})
export type DXFEdgeBanding = typeof DXFEdgeBanding.Type

export const ShapeDimensionDefaults = S.Struct({
  width: S.optionalKey(S.PositiveInt),
  height: S.optionalKey(S.PositiveInt),
  depth: S.optionalKey(S.PositiveInt),
  diameter: S.optionalKey(S.PositiveInt),
  radius: S.optionalKey(S.PositiveInt),
  base: S.optionalKey(S.PositiveInt),
  cornerRadius: S.optionalKey(S.NonNegativeInt),
  cornerRadiusTL: S.optionalKey(S.NonNegativeInt),
  cornerRadiusTR: S.optionalKey(S.NonNegativeInt),
  cornerRadiusBR: S.optionalKey(S.NonNegativeInt),
  cornerRadiusBL: S.optionalKey(S.NonNegativeInt),
  bevel: S.optionalKey(S.NonNegativeInt),
  bevelTL: S.optionalKey(S.NonNegativeInt),
  bevelTR: S.optionalKey(S.NonNegativeInt),
  bevelBR: S.optionalKey(S.NonNegativeInt),
  bevelBL: S.optionalKey(S.NonNegativeInt),
  shortEdge: S.optionalKey(S.PositiveInt),
  topWidth: S.optionalKey(S.PositiveInt),
  bottomWidth: S.optionalKey(S.PositiveInt),
  offset: S.optionalKey(S.Int),
  outerRadius: S.optionalKey(S.PositiveInt),
  innerRadius: S.optionalKey(S.PositiveInt),
  points: S.optionalKey(S.PositiveInt),
  sides: S.optionalKey(S.PositiveInt)
})

// Mode: preview only (frontend), generate and upload 3D (server-side), or generate and upload 2D (server-side, flat DXF)
export const DXFMode = S.Literals(["previewOnly", "generateAndUpload", "generateAndUpload2D"])
export type DXFMode = typeof DXFMode.Type

export const PreviewMeasureUnitBase = S.Literals(["mm", "cm", "m"])
export type PreviewMeasureUnitBase = typeof PreviewMeasureUnitBase.Type

export const PreviewMeasureUnitLabel = S.Union([PreviewMeasureUnitBase, S.Literals(["auto"])])
export type PreviewMeasureUnitLabel = typeof PreviewMeasureUnitLabel.Type

export class DXFConfig extends S.Opaque<DXFConfig, DXFConfig.Encoded>()(
  S
    .Struct({
      enabled: S.Boolean.withConstructorDefault,
      mode: DXFMode.pipe(S.withConstructorDefault(Effect.succeed("generateAndUpload2D" as const))),
      previewMeasureUnit: S
        .Struct({
          base: PreviewMeasureUnitBase.pipe(S.withConstructorDefault(Effect.succeed("cm" as const))),
          label: PreviewMeasureUnitLabel.pipe(S.withConstructorDefault(Effect.succeed("auto" as const)))
        })
        .pipe(S.withConstructorDefault(Effect.succeed({ base: "cm" as const, label: "auto" as const }))),

      // Shape
      defaultShape: DXFShapeType.pipe(S.withConstructorDefault(Effect.succeed("rectangle" as const))),
      shapeOption: S
        .NullOr(S.Struct({
          controlledBy: S.StringId,
          shapeMapping: S.mutable(S.Array(S.Struct({
            optionItemId: S.StringId,
            shape: DXFShapeType
          })))
        }))
        .withConstructorDefault,

      // Color
      defaultMeshColor: DXFColorType.pipe(S.withConstructorDefault(Effect.succeed("blue" as const))),
      meshColorOption: S
        .NullOr(S.Struct({
          controlledBy: S.StringId,
          colorMapping: S.mutable(S.Array(S.Struct({
            optionItemId: S.StringId,
            color: DXFColorType
          })))
        }))
        .withConstructorDefault,
      // Applied as a rotation delta on top of decor image direction (see DXFGrainDirection above).
      defaultGrainDirection: DXFGrainDirection.pipe(
        S.withConstructorDefault(Effect.succeed("length" as const)),
        S.withDecodingDefault(Effect.succeed("length" as const))
      ),
      grainDirectionOption: S
        .NullOr(DXFGrainDirectionOption)
        .pipe(
          S.withConstructorDefault(Effect.succeed(null as DXFGrainDirectionOption | null)),
          S.withDecodingDefault(Effect.succeed(null as DXFGrainDirectionOption | null))
        ),
      annotationColor: HexColor.pipe(S.withConstructorDefault(Effect.succeed("#ff0000"))),
      outlineColor: HexColor.pipe(S.withConstructorDefault(Effect.succeed("#000000"))),
      backgroundColor: HexColor.pipe(S.withConstructorDefault(Effect.succeed("#f5f5f5"))),
      backgroundColorOption: S
        .NullOr(S.Struct({
          controlledBy: S.StringId,
          colorMapping: S.mutable(S.Array(S.Struct({
            optionItemId: S.StringId,
            color: HexColor
          })))
        }))
        .withConstructorDefault,

      // Material
      defaultMaterialEffect: DXFMaterialEffectType.pipe(S.withConstructorDefault(Effect.succeed("none" as const))),
      materialEffectOption: S
        .NullOr(S.Struct({
          controlledBy: S.StringId,
          materialMapping: S.mutable(S.Array(S.Struct({
            optionItemId: S.StringId,
            materialEffect: DXFMaterialEffectType
          })))
        }))
        .withConstructorDefault,

      // Decor mapping. top/bottom = surfaces (Dekor card). north/south/east/west =
      // per-side edge override (Bekantung card, "Pro Fläche" mode): applied only
      // when edgeBanding is null. optionalKey per face so partial objects validate.
      decorOption: S
        .NullOr(S.Struct({
          top: S.optionalKey(S.NullOr(DecorOptionLink)),
          bottom: S.optionalKey(S.NullOr(DecorOptionLink)),
          north: S.optionalKey(S.NullOr(DecorOptionLink)),
          south: S.optionalKey(S.NullOr(DecorOptionLink)),
          east: S.optionalKey(S.NullOr(DecorOptionLink)),
          west: S.optionalKey(S.NullOr(DecorOptionLink))
        }))
        .withConstructorDefault,
      // Standard Dekor (CFG-167): an item of the decor option whose decor applies while
      // nothing mapped is selected (and in the backoffice preview); null = mesh color.
      defaultDecorItemId: S.NullOr(S.StringId).pipe(
        S.withConstructorDefault(Effect.succeed(null as typeof S.StringId.Type | null)),
        S.withDecodingDefault(Effect.succeed(null as typeof S.StringId.Type | null))
      ),

      // Dimensions
      shapeDefaults: S.NullOr(S.Record(DXFShapeType, ShapeDimensionDefaults)).withConstructorDefault,
      dimensionOptions: S
        .NullOr(S.Struct({
          width: DimensionOptionLink,
          height: DimensionOptionLink,
          depth: DimensionOptionLink,
          diameter: DimensionOptionLink,
          radius: DimensionOptionLink,
          base: DimensionOptionLink,
          cornerRadius: DimensionOptionLink,
          cornerRadiusTL: DimensionOptionLink,
          cornerRadiusTR: DimensionOptionLink,
          cornerRadiusBR: DimensionOptionLink,
          cornerRadiusBL: DimensionOptionLink,
          bevel: DimensionOptionLink,
          bevelTL: DimensionOptionLink,
          bevelTR: DimensionOptionLink,
          bevelBR: DimensionOptionLink,
          bevelBL: DimensionOptionLink,
          shortEdge: DimensionOptionLink,
          topWidth: DimensionOptionLink,
          bottomWidth: DimensionOptionLink,
          offset: DimensionOptionLink,
          outerRadius: DimensionOptionLink,
          innerRadius: DimensionOptionLink,
          points: DimensionOptionLink,
          sides: DimensionOptionLink
        }))
        .withConstructorDefault,

      // Viewer Defaults
      defaultWireframe: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
      defaultCameraPreset: DXFCameraPreset.pipe(S.withConstructorDefault(Effect.succeed("top" as const))),

      // Camera focus rules: when the focused option matches controlledBy, look from `directions`.
      // A direction is one of the six axis viewpoints (its values coincide with the slab faces);
      // multiple directions are blended into a single camera position by focusCameraTransform.
      // `zoom` (optional, > 1) aims at the edge/corner the directions point to and moves in
      // by that factor, to show details like the edge banding.
      // `optionItemId` (optional, list options) applies the rule only while that item is
      // selected; the rules of all selected items combine. null = the option-wide rule, used
      // when no selected item has its own (resolveCameraFocus).
      cameraFocusRules: S
        .NullOr(S.mutable(S.Array(S.Struct({
          controlledBy: S.StringId,
          optionItemId: S.NullOr(S.StringId).pipe(
            S.withConstructorDefault(Effect.succeed(null as typeof S.StringId.Type | null)),
            S.withDecodingDefault(Effect.succeed(null as typeof S.StringId.Type | null))
          ),
          directions: S.mutable(S.Array(DXFFaceDirection)),
          zoom: S.NullOr(S.Number).pipe(
            S.withConstructorDefault(Effect.succeed(null as number | null)),
            S.withDecodingDefault(Effect.succeed(null as number | null))
          )
        }))))
        .withConstructorDefault,

      // Edge banding (Bekantung): owns the 4 edge faces. Resolved only in the 3D preview.
      edgeBanding: S
        .NullOr(DXFEdgeBanding)
        .pipe(
          S.withConstructorDefault(Effect.succeed(null as DXFEdgeBanding | null)),
          S.withDecodingDefault(Effect.succeed(null as DXFEdgeBanding | null))
        ),

      defaultShowMeasurements: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
      defaultShowMinimap: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(false))),
      defaultShow3DLogo: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(false))),

      // Preview Controls
      previewControls: S
        .Struct({
          zoom: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
          camera: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
          wireFrame: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
          miniMap: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
          threeDLogo: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
          mass: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
          hiddenMeasurements: S.mutable(S.Array(DXFMeasurements)).pipe(
            S.withConstructorDefault(Effect.succeed([] as (typeof DXFMeasurements.Type)[]))
          ),
          visibility: DXFPreviewControlsVisibility.pipe(S.withConstructorDefault(Effect.succeed("always" as const)))
        })
        .pipe(S.withConstructorDefault(Effect.succeed({
          zoom: true,
          camera: true,
          wireFrame: true,
          miniMap: true,
          threeDLogo: true,
          mass: true,
          hiddenMeasurements: [] as (typeof DXFMeasurements.Type)[],
          visibility: "always" as const
        })))
    })
    .annotate({ identifier: "DXFConfig" })
) {}

// codegen:start {preset: model}
//
/* eslint-disable */
export namespace DXFConfig {
  export interface Encoded extends S.Struct.Encoded<typeof DXFConfig["fields"]> {}
}
/* eslint-enable */
//
// codegen:end

// Partial-update payload for UpdateDXFConfig (all fields exact-optional). Named/opaque
// (like DXFConfig itself) so TS can print its type by reference instead of expanding the
// full struct inline at every call site — without this, RPC resources consuming it hit
// TS7056 "the inferred type of this node exceeds the maximum length the compiler will serialize".
export class DXFConfigUpdate extends S.Opaque<DXFConfigUpdate, DXFConfigUpdate.Encoded>()(
  S.Struct(S.makeExactOptional(DXFConfig.fields)).annotate({ identifier: "DXFConfigUpdate" })
) {}

// codegen:start {preset: model}
//
/* eslint-disable */
export namespace DXFConfigUpdate {
  export interface Encoded extends S.Struct.Encoded<typeof DXFConfigUpdate["fields"]> {}
}
/* eslint-enable */
//
// codegen:end
